import React, { useEffect, useState, useCallback } from "react";
import { Search, Trash2, CheckCircle } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { Link } from "react-router-dom";
import axios from "axios";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import { DownArrow, PlusIcon, UpArrow } from "../svg/Icons";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import { styles } from "../../styles/pages/Opportunity/OpportunityTableStyles";
import LogButton from "../common/LogButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faEye } from "@fortawesome/free-solid-svg-icons";
import Loader from "../common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

interface APIOpportunitie {
  id: number;
  opportunitie_name: string;
  opp_amount: string;
  expected_close_date: string;
  probability: string;
  status: string;
  stage: string;
  source: string;
  assigned_to: string;
  log: string;
}

interface Opportunitie {
  id: number;
  opportunitie_name: string;
  opp_amount: string;
  expected_close_date: string;
  probability: string;
  status: string;
  stage: string;
  source: string;
  // assigned_to: string;
  log: string;
}

const OpportunityTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [opportunities, setOpportunities] = useState<Opportunitie[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Opportunitie | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedOpportunitieId, setSelectedOpportunitieId] = useState<
    number | null
  >(null);
  const [isDeleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);

  const getAuthToken = (): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      console.error("No auth token found.");
    }
    return token;
  };

  const axiosConfig = (token: string) => ({
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    setError(null);

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/opportunitie/search/list`,
        { search: "" },
        axiosConfig(token)
      );

      if (response.data.status === 200) {
        const formattedOpportunities: Opportunitie[] = Array.isArray(
          response.data.data
        )
          ? response.data.data
            .map((opportunitie: APIOpportunitie) => ({
              id: opportunitie.id,
              opportunitie_name: opportunitie.opportunitie_name,
              opp_amount: opportunitie.opp_amount,
              expected_close_date: opportunitie.expected_close_date,
              probability: opportunitie.probability,
              status: opportunitie.status,
              stage: opportunitie.stage,
              source: opportunitie.source,
              // assigned_to: opportunitie.assigned_to,
              log: opportunitie.log,
            }))
            .sort((a: Opportunitie, b: Opportunitie) => b.id - a.id)
          : [];
        setOpportunities(formattedOpportunities);
      } else {
        setOpportunities([]);
      }
    } catch (err) {
      setError(
        axios.isAxiosError(err) && err.response
          ? err.response.data.message || "Failed to fetch opportunities."
          : "An unknown error occurred."
      );
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConfirmDelete = async () => {
    if (!selectedOpportunitieId) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/opportunitie/delete`,
        { opportunitie_id: selectedOpportunitieId },
        axiosConfig(token)
      );

      if (response.data.status === 200) {
        setDeleteSuccessModalOpen(true);
        setTimeout(() => setDeleteSuccessModalOpen(false), 2000);

        setOpportunities((prevOpportunities) => {
          const updatedOpportunities = prevOpportunities.filter(
            (opportunitie) => opportunitie.id !== selectedOpportunitieId
          );
          if (
            updatedOpportunities.length > 0 &&
            displayedOpportunities.length === 1
          ) {
            setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
          }
          return updatedOpportunities;
        });
        closeModal(); // Close confirmation modal
        setDeleteSuccessModalOpen(true); // Open success modal
        setTimeout(() => setDeleteSuccessModalOpen(false), 2000);
      }
    } catch (err) {
      console.error("Error deleting opportunitie:", err);
      alert(
        axios.isAxiosError(err) && err.response
          ? err.response.data.message || "Failed to delete opportunitie."
          : "An unexpected error occurred."
      );
    }
  };

  const filteredOpportunities = opportunities.filter((opportunitie) =>
    Object.values(opportunitie)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const sortedOpportunities = [...filteredOpportunities].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    return typeof valA === "string" && typeof valB === "string"
      ? sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA)
      : sortOrder === "asc"
        ? (valA as number) - (valB as number)
        : (valB as number) - (valA as number);
  });

  // Disable pagination on tablets (below md: 768px) and force full data
  const isTabletView = window.innerWidth < 768;
  const displayedOpportunities = isTabletView
    ? sortedOpportunities
    : sortedOpportunities.slice(
      (currentPage - 1) * rowsPerPage,
      currentPage * rowsPerPage
    );
  const totalPages = isTabletView
    ? 1
    : Math.ceil(filteredOpportunities.length / rowsPerPage);

  const handleDeleteClick = (opportunitieId: number) => {
    setSelectedOpportunitieId(opportunitieId);
    openModal();
  };

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);

  const handleSort = (column: keyof Opportunitie) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredOpportunities
      : sortColumn
        ? sortedOpportunities
        : opportunities;

    if (!dataToDownload.length) {
      alert("No data available to download.");
      return;
    }

    const formattedData = dataToDownload.map((opportunitie) => ({
      "Opportunity Name": opportunitie.opportunitie_name,
      Amount: new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
      }).format(Number(opportunitie.opp_amount)),
      "Expected Close Date": opportunitie.expected_close_date,
      Probability: opportunitie.probability ? `${opportunitie.probability}%` : "0%",
      Source: opportunitie.source,
      Stage: opportunitie.stage,
      Status: opportunitie.status,
      // "Assigned To": opportunitie.assigned_to,
      Log: opportunitie.log,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title row
    XLSX.utils.sheet_add_aoa(worksheet, [["Opportunity List"]], {
      origin: "A1",
    });

    // 2. Merge title row across all columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // 3. Style title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add header row
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Style header cells
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C, ...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data rows
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) =>
              (row[key as keyof typeof row] as string)?.toString().length || 0
          ),
          "Opportunity List".length
        ) + 2,
    }));

    // 8. Create and export workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Opportunities");
    XLSX.writeFile(
      workbook,
      searchTerm
        ? `OpportunitieData_${searchTerm}.xlsx`
        : "OpportunitieData.xlsx"
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, isTabletView]);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Opportunity List</h3>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.headerControls}>
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
          />
          <div className={styles.controlsInner}>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className={styles.downloadButton} onClick={handleDownload}>
              Download
              <DownloadIcon className={styles.downloadIcon} />
            </button>
            <Link to="/opportunity-add" className={styles.addButtonContainer}>
              <button className={styles.addButton}>
                Add New Opportunity
                <PlusIcon />
              </button>
            </Link>
          </div>
        </div>
        {error && <p className={styles.errorText}>{error}</p>}

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow}>
                <th
                  className={styles.thName}
                  onClick={() => handleSort("opportunitie_name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Name</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thAmount}
                  onClick={() => handleSort("opp_amount")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Amount</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thECD}
                  onClick={() => handleSort("expected_close_date")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Expected Close Date</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thTexts}
                  onClick={() => handleSort("probability")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Probability</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thTexts}
                  onClick={() => handleSort("source")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Source</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thTexts}
                  onClick={() => handleSort("stage")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Stage</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thTexts}
                  onClick={() => handleSort("status")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Status</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                {/* <th
                  className={styles.thAssign}
                  onClick={() => handleSort("assigned_to")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Assigned To</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th> */}
                <th className={styles.thAction}>
                  <p className={styles.thText}>Action</p>
                </th>
                <th className={styles.thLog}>
                  <p className={styles.thText}>Log</p>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr className={styles.loadingRow}>
                  <td colSpan={10} className={styles.loadingText}>
                    <Loader />
                  </td>
                </tr>
              ) : displayedOpportunities.length > 0 ? (
                displayedOpportunities.map((opportunitie) => (
                  <tr key={opportunitie.id} className={styles.tbodyRow}>
                    <td className={styles.tdName}>
                      {opportunitie.opportunitie_name}
                    </td>
                    <td className={styles.tdName}>
                      ₹
                      {parseFloat(opportunitie.opp_amount).toLocaleString(
                        "en-IN",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      )}
                    </td>
                    <td className={styles.tdName}>
                      {opportunitie.expected_close_date}
                    </td>
                    <td className={styles.tdName}>
                      {opportunitie.probability}%
                    </td>
                    <td className={styles.tdName}>{opportunitie.source}</td>
                    <td className={styles.tdName}>{opportunitie.stage}</td>
                    <td className={styles.tdName}>{opportunitie.status}</td>
                    {/* <td className={styles.tdName}>
                      {opportunitie.assigned_to}
                    </td> */}
                    <td className={styles.tdAction}>
                      <div className={styles.actionContainer}>
                        <Tooltip content="View" bgColor="bg-green-600">
                          <Link
                            to={`/opportunity-view`}
                            state={{ opportunitie_id: opportunitie.id }}
                            className="inline-flex"
                          >
                            <button className={styles.viewButton}>
                              <FontAwesomeIcon
                                icon={faEye}
                                className="text-white text-sm"
                              />
                            </button>
                          </Link>
                        </Tooltip>
                        {opportunitie.status !== "Converted" && (
                          <Tooltip content="Edit" bgColor="bg-[#2f6aa7]">
                            <Link
                              to={`/opportunity-edit/${opportunitie.id}`}
                              className="inline-flex"
                            >
                              <button className={styles.editButton}>
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-white text-sm"
                                />
                              </button>
                            </Link>
                          </Tooltip>
                        )}

                        <Tooltip content="Delete" bgColor="bg-red-600">
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleDeleteClick(opportunitie.id)}
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              className="text-white text-sm"
                            />
                          </button>
                        </Tooltip>
                      </div>
                    </td>

                    <td className={styles.tdLog}>
                      <div className={styles.actionContainer}>
                        <LogButton log={opportunitie.log} />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className={styles.loadingRow}>
                  <td colSpan={10} className={styles.noDataText}>
                    No records match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!isTabletView && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            filteredLength={filteredOpportunities.length}
            handlePageChange={handlePageChange}
          />
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={closeModal} className={styles.modal}>
        <button
          onClick={closeModal}
          className={styles.modalCloseButton}
        ></button>
        <div className="flex justify-center">
          <div className={styles.modalIconContainer}>
            <Trash2 className={styles.modalIcon} />
          </div>
        </div>
        <h4 className={styles.modalTitle}>Confirm Delete</h4>
        <p className={styles.modalText}>
          Are you sure you want to delete this opportunitie?
        </p>
        <div className={styles.modalButtonContainer}>
          <button onClick={closeModal} className={styles.modalCancelButton}>
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </Modal>

      {/* Delete Success Modal */}
      <Modal
        isOpen={isDeleteSuccessModalOpen}
        onClose={() => setDeleteSuccessModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-lg font-bold text-gray-800 dark:text-white mt-4">
          Opportunity Deleted Successfully!
        </h4>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setDeleteSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default OpportunityTable;
